<template>
  <svg height="100%" width="100%"
    :viewBox="`${vboxLeft} ${vboxTop} ${vboxWidth} ${vboxHeight}`"
  >
    <g :transform="`scale(1,-1) translate(0,-${size[1]*height_})`">
      <text
        :x="scale.ax(0.5)"
        :y="bottomMargin-2"
        text-anchor="middle"
        transform="scale(1,-1)"
        style="font-size:10pt;"
      >{{xTitle}}</text>
      <text
        x="0.0"
        y="0.0"
        text-anchor="middle"
        :transform="`translate(${vboxLeft+20},${scale.ay(0.5)}) rotate(90) scale(1,-1)`"
        style="font-size:10pt;"
      >{{yTitle}}</text>
      <g v-for="tick in xTicks" :transform="`translate( ${scale.x(tick)},0)`">
        <path :d="`M 0,-4 L 0,0`" style="fill:none;stroke:#000000;stroke-width:1px" />
        <text x="0" :y="20" text-anchor="middle" transform="scale(1,-1)">{{tick}}</text>
      </g>
      <g v-for="tick in yTicks" :transform="`translate(0, ${scale.y(tick)})`">
        <path :d="`M -4,0 L 0,0`" style="fill:none;stroke:#000000;stroke-width:1px" />
        <text x="-8" y="4pt" text-anchor="end" transform="scale(1,-1)">{{tick}}</text>
      </g>
      <path :d="`M ${scale.x(xTicks[0])},0 L ${scale.x(xTicks[xTicks.length-1])},0`" style="fill:none;stroke:#000000;stroke-width:1px" />
      <path :d="`M 0,${scale.y(yTicks[0])} L 0,${scale.y(yTicks[yTicks.length-1])}`" style="fill:none;stroke:#000000;stroke-width:1px" />
      <path v-for="(path, i) in paths" :d="path" :style="`fill:none;stroke:${colorAtIndex(i)};stroke-width:2px`" />
    </g>
  </svg>
</template>

<script>
import * as d3 from "d3";

function axisTicks(min, max, n) {
  const range = max - min;
  const exp = Math.floor(Math.log10(range - 0.000000000001));
  const x = range / Math.pow(10, exp) / n;
  var step;
  if (x < 0.15) {
    step = 0.1 * Math.pow(10, exp);
  } else if (x < 0.33) {
    step = 0.2 * Math.pow(10, exp);
  } else if (x < 0.7) {
    step = 0.5 * Math.pow(10, exp);
  } else {
    step = Math.pow(10, exp);
  }

  var ticks = [];
  const nDec = exp <= 0 ? Math.abs(exp)+1 : 0;
  for (var i = Math.floor(min / step); i <= Math.ceil(max / step); i++) {
    ticks.push((i * step).toFixed(nDec));
  }
  return ticks;
}

export default {
  name: "Chart",
  props: {
    curves: {
      type: Array,
      default: []
    },
    left: {
      type: Number,
      default: null
    },
    bottom: {
      type: Number,
      default: null
    },
    height: {
      type: Number,
      default:null
    },
    width: {
      type: Number,
      default: null
    },
    xTitle: {
      type: String,
      default: ""
    },
    yTitle: {
      type: String,
      default: ""
    },
    xKey: {
      type: [String,Number],
      default: 0
    },
    yKey: {
      type: [String,Number],
      default: 1
    },
    xMultiplier: {
      type: Number,
      default: 1
    },
    yMultiplier: {
      type: Number,
      default: 1
    },
    resizeTrigger: {}
  },
  data: function() {
    return {
      size: [100, 100]
    };
  },
  computed: {
    curves_: function() {
        if (!Array.isArray(this.curves[0])) return [this.curves]
        return this.curves
    },
    dataBounds: function() {
      var bounds = [
        Number.POSITIVE_INFINITY,
        Number.NEGATIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        Number.NEGATIVE_INFINITY
      ];
      for (var i = 0; i < this.curves_.length; i++) {
        bounds[0] = Math.min(
          bounds[0],
          Math.min(...this.curves_[i].map(x => x[this.xKey]*this.xMultiplier))
        );
        bounds[1] = Math.max(
          bounds[1],
          Math.max(...this.curves_[i].map(x => x[this.xKey]*this.xMultiplier))
        );
        bounds[2] = Math.min(
          bounds[2],
          Math.min(...this.curves_[i].map(x => x[this.yKey]*this.yMultiplier))
        );
        bounds[3] = Math.max(
          bounds[3],
          Math.max(...this.curves_[i].map(x => x[this.yKey]*this.yMultiplier))
        );
      }
      if (bounds[0] === Number.POSITIVE_INFINITY) {
        bounds[0] = 0;
      }
      if (bounds[1] === Number.NEGATIVE_INFINITY) {
        bounds[1] = 1;
      }
      if (bounds[2] === Number.POSITIVE_INFINITY) {
        bounds[2] = 0;
      }
      if (bounds[3] === Number.NEGATIVE_INFINITY) {
        bounds[3] = 1;
      }
      return bounds;
    },
    scale: function() {
      const x = d3
        .scaleLinear().range([0, this.size[0]*this.width_])
        .domain([0, this.xTicks[this.xTicks.length - 1]]);
      const y = d3
        .scaleLinear().range([0, this.size[1]*this.height_])
        .domain([this.yTicks[0], this.yTicks[this.yTicks.length - 1]]);
      const ax = d3
        .scaleLinear().range([0, this.size[0]*this.width_]);
      const ay = d3
        .scaleLinear().range([0, this.size[1]*this.height_]);
      return { x: x, y: y, ax: ax, ay: ay};
    },
    paths: function() {
      var paths = [];
      for (var i = 0; i < this.curves_.length; i++) {
        const path = d3
          .line()
          .x(d => this.scale.x(d[this.xKey]*this.xMultiplier))
          .y(d => this.scale.y(d[this.yKey]*this.yMultiplier));
        paths.push(path(this.curves_[i]));
      }
      return paths;
    },
    xTicks: function() {
      return axisTicks(0, this.dataBounds[1], 10);
    },
    yTicks: function() {
      return axisTicks( this.dataBounds[2], this.dataBounds[3], 8);
    },
    left_: function() {
      if (this.left !== null) return this.left
      try {
        const textWidth = 8*(this.yTicks[this.yTicks.length-1]).toString().length
        return (textWidth+15+20)/this.size[0]
      }
      catch {
        return 0.2
      }
    },
    bottom_: function() {
      if (this.bottom !== null) return this.bottom
      if (!this.size[1])  return 0.15
      return (20+20)/this.size[1]
    },
    width_: function() {
      if (this.width !== null) return this.width
      if (!this.size[0])  return 0.7
      return 1-this.left_-20/this.size[0]
    },
    height_: function() {
      if (this.height !== null) return this.height
      if (!this.size[1])  return 0.7
      return 1-this.bottom_-20/this.size[1]
    },
    vboxLeft: function() {
      return -this.left_*this.size[0];
    },
    vboxTop: function() {
      return -(1-this.height_-this.bottom_)*this.size[1];
    },
    vboxWidth: function() {
      return this.size[0];
    },
    vboxHeight: function() {
      return this.size[1];
    },
    bottomMargin: function() {
      return this.bottom_*this.size[1];
    }
  },
  methods: {
    colorAtIndex(i) {
      if (i==0) return '#3b8ede'
      if (i==1) return '#ccbde9'
      if (i==2) return '#badc66'
      return '#000000'
    },
    onResize(event) {
        this.$set(this.size, 0, this.$el.clientWidth);
        this.$set(this.size, 1, this.$el.clientHeight);
    }
  },
  mounted() {
    this.$nextTick(function() {
        window.addEventListener('resize', this.onResize);
        this.onResize(null);
    })
  },
  beforeUpdate() {
    this.onResize(null);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.getWindowWidth);
    window.removeEventListener("resize", this.getWindowHeight);
  },
  watch: {
    resizeTrigger: function() {
      this.onResize(null);
    }
  }
};
</script>

<style scoped>
</style>